import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import WhiteboardToolbar from "./whiteboard/WhiteboardToolbar";
import { CREATIVE_STARTS } from "./whiteboard/CreativeStarts";
import { BoardElement, SelectionHandles } from "./whiteboard/elements";
import {
  buildFramework, buildFilledFramework, FRAMEWORKS, tagToolbarFrameworks, toolbarFrameworkTag,
} from "./whiteboard/frameworks";
import ChartDataEditor from "./whiteboard/ChartDataEditor";
import VersionHistory from "./whiteboard/VersionHistory";
import ExportPanel from "./whiteboard/ExportPanel";
import ImagePromptBar from "./whiteboard/ImagePromptBar";
import UnsplashPanel from "./whiteboard/UnsplashPanel";
import AdjustBar from "./whiteboard/AdjustBar";
import { adjustSelection } from "../services/aiAdjustService";
import { searchPhotos, trackDownload, attributionFor } from "../services/unsplashService";
import { generateImage, persistGeneratedImage } from "../services/imageGenerationService";
import {
  regionOf, regionToPngBlob, regionToJpegDataUrl, regionToDataUrl, renderBoardSnapshot,
  imageToPdfBlob, downloadBlob,
} from "./whiteboard/exportBoard";
import { makeChart } from "./whiteboard/charts";
import {
  TEAL, BOARD_BG, CANVAS_W, CANVAS_H, MIN_SCALE, MAX_SCALE, UNDO_LIMIT,
  GRID_MODES, STICKY_COLORS, uid, toCanvas, elementBounds,
  connectorEndpoints, distanceToSegment,
} from "./whiteboard/constants";
import {
  STAMP_TOOLS, SHAPE_TOOLS, LINE_TOOLS, KEY_TO_TOOL, CHART_TOOLS, CONNECTOR_TOOLS,
  IMAGE_SLOT_TOOL,
} from "./whiteboard/toolConfig";
import {
  emptyBoard, loadWhiteboard, saveWhiteboard,
  saveVersion, listVersions, loadVersion,
} from "../services/whiteboardService";
import { uploadRoomFile, getRoomFileUrl } from "../lib/roomFileStorage";
import { describeBoard } from "./whiteboard/describeBoard";

const AUTOSAVE_MS = 1200;

const placedCards = new Map();
const placedCardsFor = (userId, roomId) => {
  const key = `${userId || ""}:${roomId || ""}`;
  if (!placedCards.has(key)) placedCards.set(key, new Set());
  return placedCards.get(key);
};

const STAMP_SIZES = {
  sticky:  { w: 130, h: 110 },
  sticker: { w: 60,  h: 60  },
  emoji:   { w: 60,  h: 60  },
  tape:    { w: 160, h: 42  },
  frame:   { w: 220, h: 160 },
};

function textCapacity(el) {
  const sticky = el.type === "sticky";
  const fontSize = sticky ? Math.max(11, (el.size || 2.4) * 5) : Math.max(12, (el.size || 3) * 5);
  const innerW = Math.max(40, Math.abs(el.w || 200) - (sticky ? 20 : 0));
  const innerH = Math.max(fontSize * 1.4, Math.abs(el.h || 40) - (sticky ? 20 : 0));
  const perLine = Math.max(8, Math.floor(innerW / (fontSize * 0.55)));
  const lines = sticky ? Math.max(2, Math.floor(innerH / (fontSize * 1.35))) : 2;
  return Math.max(12, Math.min(280, Math.floor(perLine * lines * 0.9)));
}

function fitInside(region, aspect) {
  if (!Number.isFinite(aspect) || aspect <= 0) return region;
  const w = Math.min(region.w, region.h * aspect);
  const h = w / aspect;
  return { x: region.x + (region.w - w) / 2, y: region.y + (region.h - h) / 2, w, h };
}

export default function WhiteboardPanel({
  onClose,
  externalCards,
  userId,
  roomId,
  onAskLumen,
  onSendToDesk,
  onSendToBookshelf,
  onContentsChange,
  onStartsChange,
  onSnapshot,
  hasImage = false,
  roomContext,
}) {
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);

  const [vp, setVp]             = useState({ x: 0, y: 0, scale: 1 });
  const [elements, setElements] = useState([]);
  const [history, setHistory]   = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const historyTickRef = useRef(0);
  const [historyTick, setHistoryTick] = useState(0);

  const [category, setCategory] = useState("standard");
  const [tool, setTool]   = useState("hand");
  const [color, setColor] = useState("#1E293B");
  const [size, setSize]   = useState(3);
  const [glyph, setGlyph] = useState("★");
  const [gridMode, setGridMode] = useState("dots");

  const [selectedIds, setSelectedIds]   = useState([]);
  const [dragging, setDragging]         = useState(null);
  const [resizing, setResizing]         = useState(null);
  const [rubberBand, setRubberBand]     = useState(null);
  const [panning, setPanning]           = useState(null);
  const [drawing, setDrawing]           = useState(false);
  const [currentStroke, setCurrentStroke] = useState(null);
  const [drawingLine, setDrawingLine]   = useState(null);
  const [drawingShape, setDrawingShape] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);
  const [spaceDown, setSpaceDown]       = useState(false);
  const [cropDraft, setCropDraft]       = useState(null);
  const [connectorFrom, setConnectorFrom] = useState(null);
  const [pointerPos, setPointerPos]     = useState(null);
  const [overEmpty, setOverEmpty]       = useState(false);
  const [activeSlotId, setActiveSlotId] = useState(null);
  const [imageUsage, setImageUsage]     = useState(null);
  const [unsplashOpen, setUnsplashOpen] = useState(false);
  const [adjustBusy, setAdjustBusy]     = useState(false);
  const [adjustError, setAdjustError]   = useState(null);
  const [adjustTargetIds, setAdjustTargetIds] = useState([]);
  const [adjustDone, setAdjustDone] = useState(null);
  const [retryRequest, setRetryRequest] = useState(null);
  const [adjustBarKey, setAdjustBarKey] = useState(0);
  const lumenSelectionRef = useRef(null);
  const [editingChartId, setEditingChartId] = useState(null);

  const [whiteboardId, setWhiteboardId]   = useState(null);
  const whiteboardIdRef = useRef(null);
  const rememberWhiteboardId = useCallback((id) => {
    if (!id) return;
    whiteboardIdRef.current = id;
    setWhiteboardId(id);
  }, []);
  const [historyOpen, setHistoryOpen]     = useState(false);
  const [versions, setVersions]           = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [savingVersion, setSavingVersion] = useState(false);
  const [restoringId, setRestoringId]     = useState(null);

  const [exportOpen, setExportOpen]   = useState(false);
  const [exportScope, setExportScope] = useState("selection");
  const [exportBusy, setExportBusy]   = useState(null);
  const [exportError, setExportError] = useState(null);
  const svgRef = useRef(null);
  const [cropHandle, setCropHandle]     = useState(null);

  const [loading, setLoading]     = useState(Boolean(userId && roomId));
  const [saveState, setSaveState] = useState("idle");

  const dirtyRef    = useRef(false);
  const saveTimer   = useRef(null);
  const boardRef    = useRef({ elements: [], vp, gridMode });
  const loadedRef   = useRef(false);

  boardRef.current = { elements, vp, gridMode };

  useEffect(() => {
    let cancelled = false;
    if (!userId || !roomId) { setLoading(false); loadedRef.current = true; return; }

    (async () => {
      let saved = null;
      try {
        saved = await loadWhiteboard(userId, roomId);
      } catch (err) {
        console.warn("[whiteboard] load threw:", err?.message || err);
      }
      if (cancelled) return;
      if (saved?.id) rememberWhiteboardId(saved.id);
      if (saved?.board) {
        setElements(tagToolbarFrameworks(saved.board.elements || []));
        if (saved.board.viewport) setVp(saved.board.viewport);
        if (saved.board.gridMode) setGridMode(saved.board.gridMode);
      }
      setLoading(false);
      loadedRef.current = true;
    })();

    return () => { cancelled = true; };
  }, [userId, roomId]);

  const flushSave = useCallback(async () => {
    clearTimeout(saveTimer.current);
    if (!dirtyRef.current || !userId || !roomId) return;
    dirtyRef.current = false;
    setSaveState("saving");
    const { elements: els, vp: viewport, gridMode: grid } = boardRef.current;
    let result;
    try {
      result = await saveWhiteboard(userId, roomId, {
        ...emptyBoard(), elements: els, viewport, gridMode: grid,
      });
    } catch (err) {
      console.warn("[whiteboard] autosave threw:", err?.message || err);
    }
    if (result?.id) rememberWhiteboardId(result.id);
    setSaveState(result?.ok ? "saved" : "error");
  }, [userId, roomId]);

  const markDirty = useCallback(() => {
    if (!loadedRef.current || !userId || !roomId) return;
    dirtyRef.current = true;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushSave, AUTOSAVE_MS);
  }, [flushSave, userId, roomId]);

  useEffect(() => { markDirty(); }, [elements, gridMode, markDirty]);

  const flushRef = useRef(flushSave);
  flushRef.current = flushSave;
  useEffect(() => () => {
    clearTimeout(saveTimer.current);
    flushRef.current?.();
  }, []);

  const onSnapshotRef = useRef(onSnapshot);
  onSnapshotRef.current = onSnapshot;
  const hasImageRef = useRef(hasImage);
  hasImageRef.current = hasImage;
  const roomIdRef = useRef(roomId);
  roomIdRef.current = roomId;
  const latestElementsRef = useRef(elements);
  latestElementsRef.current = elements;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const openedElementsRef = useRef(null);
  const svgNodeRef = useRef(null);

  useEffect(() => {
    if (svgRef.current) svgNodeRef.current = svgRef.current;
  });

  useEffect(() => {
    if (!loading && openedElementsRef.current === null) openedElementsRef.current = elements;
  }, [loading, elements]);

  useEffect(() => () => {
    const send = onSnapshotRef.current;
    const room = roomIdRef.current;
    if (!send || !room || loadingRef.current || openedElementsRef.current === null) return;
    const els = latestElementsRef.current;
    const changed = els !== openedElementsRef.current;
    if (!changed && hasImageRef.current) return;
    if (!els.length) {
      if (changed && hasImageRef.current) send(room, "");
      return;
    }
    renderBoardSnapshot(svgNodeRef.current, els)
      .then((content) => { if (content || changed) send(room, content || ""); })
      .catch((err) => console.warn("[whiteboard] could not capture the board for Unity:", err?.message || err));
  }, []);

  const handleClose = useCallback(async () => {
    await flushSave();
    onClose?.();
  }, [flushSave, onClose]);

  const pendingSnapshot = useRef(null);
  const snapshotCommitted = useRef(false);

  const bumpHistory = useCallback(() => {
    historyTickRef.current += 1;
    setHistoryTick(historyTickRef.current);
  }, []);

  const pushHistory = useCallback((snapshot) => {
    setHistory((h) => [...h, snapshot].slice(-UNDO_LIMIT));
    setRedoStack([]);
    bumpHistory();
  }, [bumpHistory]);

  const saveHistory = useCallback(() => pushHistory(elements), [pushHistory, elements]);

  const beginInteraction = useCallback(() => {
    pendingSnapshot.current = elements;
    snapshotCommitted.current = false;
  }, [elements]);

  const commitInteraction = useCallback(() => {
    if (snapshotCommitted.current || !pendingSnapshot.current) return;
    snapshotCommitted.current = true;
    pushHistory(pendingSnapshot.current);
  }, [pushHistory]);

  const undo = useCallback(() => {
    if (history.length === 0) return;
    setRedoStack((r) => [elements, ...r].slice(0, UNDO_LIMIT));
    setElements(history[history.length - 1]);
    setHistory((h) => h.slice(0, -1));
    setSelectedIds([]);
    bumpHistory();
  }, [history, elements, bumpHistory]);

  const redo = useCallback(() => {
    if (redoStack.length === 0) return;
    setHistory((h) => [...h, elements].slice(-UNDO_LIMIT));
    setElements(redoStack[0]);
    setRedoStack((r) => r.slice(1));
    setSelectedIds([]);
    bumpHistory();
  }, [redoStack, elements, bumpHistory]);

  const ensureWhiteboardId = useCallback(async () => {
    if (whiteboardIdRef.current) return whiteboardIdRef.current;
    if (!userId || !roomId) return null;
    const saved = await loadWhiteboard(userId, roomId);
    if (saved?.id) { rememberWhiteboardId(saved.id); return saved.id; }
    return null;
  }, [userId, roomId, rememberWhiteboardId]);

  const refreshVersions = useCallback(async (id) => {
    setVersionsLoading(true);
    const target = id || (await ensureWhiteboardId());
    setVersions(target ? await listVersions(target) : []);
    setVersionsLoading(false);
  }, [ensureWhiteboardId]);

  const openHistory = useCallback(() => {
    setHistoryOpen(true);
    refreshVersions();
  }, [refreshVersions]);

  const handleSaveVersion = useCallback(async (label) => {
    if (!userId || !roomId) return;
    setSavingVersion(true);
    dirtyRef.current = true;
    await flushSave();

    const id = await ensureWhiteboardId();
    if (id) {
      const { elements: els, vp: viewport, gridMode: grid } = boardRef.current;
      await saveVersion(userId, id, { elements: els, viewport, gridMode: grid },
        label || `Saved ${new Date().toLocaleString()}`);
      await refreshVersions(id);
    }
    setSavingVersion(false);
  }, [userId, roomId, flushSave, refreshVersions, ensureWhiteboardId]);

  const handleRestore = useCallback(async (versionId) => {
    setRestoringId(versionId);
    const snapshot = await loadVersion(versionId);
    if (snapshot?.board) {
      saveHistory();
      setElements(tagToolbarFrameworks(snapshot.board.elements || []));
      if (snapshot.board.gridMode) setGridMode(snapshot.board.gridMode);
      setSelectedIds([]);
      setEditingChartId(null);
    }
    setRestoringId(null);
  }, [saveHistory]);

  const selectedElements = useMemo(
    () => elements.filter((el) => selectedIds.includes(el.id)),
    [elements, selectedIds]
  );

  const exportRegion = useCallback(() => {
    if (exportScope === "selection" && selectedElements.length) {
      const allArtboards = selectedElements.every((el) => el.type === "artboard");
      return regionOf(selectedElements, allArtboards ? 0 : undefined);
    }
    const content = elements.filter((el) => el.type !== "artboard");
    return regionOf(content.length ? content : elements);
  }, [selectedElements, exportScope, elements]);

  const handleExport = useCallback(async (kind) => {
    const region = exportRegion();
    if (!region) {
      setExportError(exportScope === "selection" && selectedElements.length
        ? "That selection has nothing to draw. Connectors follow the objects they join, so pick those too."
        : "There is nothing on the board to export yet.");
      return;
    }

    setExportBusy(kind);
    setExportError(null);
    const previousSelection = selectedIds;
    setSelectedIds([]);
    await new Promise((r) => requestAnimationFrame(() => r()));

    try {
      const name = `whiteboard-${new Date().toISOString().slice(0, 10)}`;
      if (kind === "png") {
        const png = await regionToPngBlob(svgRef.current, region);
        if (!png) throw new Error("could not render the board");
        downloadBlob(png, `${name}.png`);
      } else if (kind === "pdf") {
        const jpeg = await regionToJpegDataUrl(svgRef.current, region);
        if (!jpeg?.width || !jpeg?.height) throw new Error("could not render the board");
        downloadBlob(
          imageToPdfBlob(jpeg.dataUrl, jpeg.width, jpeg.height, region.w, region.h),
          `${name}.pdf`
        );
      } else {
        const dataUrl = await regionToDataUrl(svgRef.current, region);
        if (!dataUrl) throw new Error("could not render the board");
        if (kind === "desk") {
          onSendToDesk?.({ title: "Whiteboard", image: dataUrl });
        } else {
          onSendToBookshelf?.({ title: "Whiteboard", image: dataUrl });
          await handleSaveVersion("Sent to Bookshelf");
        }
      }
    } catch (err) {
      console.warn("[whiteboard] export failed:", err?.message || err);
      setExportError("That export did not work. The console has the detail.");
    }

    setSelectedIds(previousSelection);
    setExportBusy(null);
  }, [exportRegion, exportScope, selectedElements, selectedIds,
      onSendToDesk, onSendToBookshelf, handleSaveVersion]);

  const getCanvasPos = useCallback((e) => {
    const rect = containerRef.current.getBoundingClientRect();
    return toCanvas(e.clientX, e.clientY, vp, rect);
  }, [vp]);

  const viewOrigin = useCallback(() => ({
    x: Math.max(0, -vp.x / vp.scale) + 90,
    y: Math.max(0, -vp.y / vp.scale) + 70,
  }), [vp]);

  const centreOnView = useCallback((els) => {
    const box = regionOf(els, 0);
    if (!box) return els;
    const rect = containerRef.current?.getBoundingClientRect();
    const viewW = rect?.width || 800;
    const viewH = rect?.height || 600;
    const cx = (viewW / 2 - vp.x) / vp.scale;
    const cy = (viewH / 2 - vp.y) / vp.scale;
    const dx = cx - box.w / 2 - box.x;
    const dy = cy - box.h / 2 - box.y;
    return els.map((el) => {
      if (el.type === "stroke") {
        return { ...el, points: (el.points || []).map((pt) => ({ ...pt, x: pt.x + dx, y: pt.y + dy })) };
      }
      return Number.isFinite(el.x) ? { ...el, x: el.x + dx, y: el.y + dy } : el;
    });
  }, [vp]);

  const centreOnViewRef = useRef(centreOnView);
  centreOnViewRef.current = centreOnView;
  const legacyMovedRef = useRef(false);

  useEffect(() => {
    if (loading) return;
    const cards = (externalCards || []).filter((c) => c?.id && (!c.room_id || c.room_id === roomId));
    const gone = new Set(cards
      .map((c) => (c.type === "remove" ? c.target : c.replaces))
      .filter(Boolean)
      .map(String));
    const onBoard = new Set(elements.map((el) => el.lumenSource).filter(Boolean));
    const legacyContent = new Set(elements
      .filter((el) => el.type === "lumen_card" && !el.lumenSource)
      .map((el) => el.content));

    const placed = placedCardsFor(userId, roomId);
    const fresh = cards.filter((c) => c.type !== "remove"
      && !gone.has(String(c.id))
      && !onBoard.has(c.id)
      && !placed.has(c.id)
      && !(c.type !== "framework" && legacyContent.has(c.content)));
    const doomed = elements.some((el) => el.lumenSource && gone.has(String(el.lumenSource)));
    const legacyOffView = legacyMovedRef.current ? [] : elements.filter((el) =>
      el.type === "lumen_card" && !el.lumenSource && el.x >= CANVAS_W * 0.7);

    onBoard.forEach((id) => placed.add(id));
    if (!fresh.length && !doomed && !legacyOffView.length) return;
    fresh.forEach((c) => placed.add(c.id));
    legacyMovedRef.current = true;

    const moveBy = (piece, dx, dy) => piece.map((el) => (el.type === "stroke"
      ? { ...el, points: (el.points || []).map((pt) => ({ ...pt, x: pt.x + dx, y: pt.y + dy })) }
      : Number.isFinite(el.x) ? { ...el, x: el.x + dx, y: el.y + dy } : el));

    const build = (c) => {
      if (c.type === "framework") {
        if (!FRAMEWORKS[c.framework]) return [];
        return buildFilledFramework(c.framework, 0, 0, c)
          .map((el) => ({ ...el, lumenSource: c.id, lumenFramework: c.framework }));
      }
      if (!c.content) return [];
      return [{
        id: uid(), type: "lumen_card", cardType: c.type || "idea", content: c.content,
        lumenSource: c.id, x: 0, y: 0, w: 210, h: 110,
      }];
    };

    const inPlace = [];
    const pieces = [];
    fresh.forEach((c) => {
      const piece = build(c);
      if (!piece.length) return;
      const old = c.replaces ? regionOf(elements.filter((el) => el.lumenSource === c.replaces), 0) : null;
      const box = regionOf(piece, 0);
      if (old && box) {
        inPlace.push(...moveBy(piece, old.x + old.w / 2 - (box.x + box.w / 2), old.y + old.h / 2 - (box.y + box.h / 2)));
      } else {
        pieces.push(piece);
      }
    });

    let cursor = 0;
    const laidOut = pieces.flatMap((piece) => {
      const box = regionOf(piece, 0);
      if (!box) return [];
      const shifted = moveBy(piece, cursor - box.x, -box.y);
      cursor += box.w + 80;
      return shifted;
    });
    const added = [...inPlace, ...(laidOut.length ? centreOnViewRef.current(laidOut) : [])];
    const moved = legacyOffView.length ? centreOnViewRef.current(legacyOffView) : [];
    const movedById = new Map(moved.map((el) => [el.id, el]));

    setElements((prev) => {
      const present = new Set(prev.map((el) => el.lumenSource).filter(Boolean));
      const kept = prev
        .filter((el) => !(el.lumenSource && gone.has(String(el.lumenSource))))
        .map((el) => movedById.get(el.id) || el);
      const toAdd = added.filter((el) => !present.has(el.lumenSource));
      return [...kept, ...toAdd];
    });
  }, [externalCards, elements, loading, roomId, userId]);

  const boardDescription = useMemo(() => describeBoard(elements), [elements]);

  useEffect(() => {
    onContentsChange?.(boardDescription);
  }, [boardDescription, onContentsChange]);

  useEffect(() => () => onContentsChange?.(null), [onContentsChange]);

  const elementsById = useMemo(
    () => new Map(elements.map((el) => [el.id, el])),
    [elements]
  );

  const hitTest = useCallback((pos) => [...elements].reverse().find((el) => {
    if (el.type === "connector") {
      const ends = connectorEndpoints(el, elementsById);
      return ends ? distanceToSegment(pos, ends.a, ends.b) <= 8 : false;
    }
    if (el.type === "line" || el.type === "arrow") {
      const a = { x: el.x, y: el.y };
      const z = { x: el.x + (el.w || 0), y: el.y + (el.h || 0) };
      return distanceToSegment(pos, a, z) <= Math.max(8, el.size || 0);
    }
    const b = elementBounds(el);
    if (!b) return false;
    const pad = el.type === "stroke" ? 10 : 0;
    return pos.x >= b.x - pad && pos.x <= b.x + b.w + pad
        && pos.y >= b.y - pad && pos.y <= b.y + b.h + pad;
  }), [elements, elementsById]);

  const addElements = useCallback((newEls, { select = true } = {}) => {
    if (!newEls.length) return;
    saveHistory();
    setElements((prev) => [...prev, ...newEls]);
    if (select) setSelectedIds(newEls.map((el) => el.id));
  }, [saveHistory]);

  const handleAdjust = useCallback(async (instruction, ids = selectedIds) => {
    const chosen = elements.filter((el) => ids.includes(el.id));
    if (!chosen.length) return;

    const bounds = regionOf(chosen, 8);
    const overlapsUnsplash = elements.some((el) => {
      if (el.attribution?.source !== "Unsplash") return false;
      const b = elementBounds(el);
      return b && bounds
        && b.x < bounds.x + bounds.w && b.x + b.w > bounds.x
        && b.y < bounds.y + bounds.h && b.y + b.h > bounds.y;
    });
    if (overlapsUnsplash) {
      setAdjustError("Unsplash photos cannot be sent to the AI, so this selection is off limits. Move it clear of the photo and try again.");
      return;
    }

    setAdjustBusy(true);
    setAdjustError(null);
    setAdjustDone(null);

    const previous = ids;
    setAdjustTargetIds(previous);
    setSelectedIds([]);
    await new Promise((r) => requestAnimationFrame(() => r()));

    const texts = chosen
      .filter((el) => el.type === "text" || el.type === "sticky")
      .map((el) => ({
        id: el.id, text: el.content || "",
        x: el.x, y: el.y, w: el.w, h: el.h,
        maxChars: textCapacity(el),
      }));

    let result;
    try {
      const region = regionOf(chosen, 8);
      const image = await regionToDataUrl(svgRef.current, region);
      result = image
        ? await adjustSelection({
          image, instruction, texts,
          context: roomContext,
          aspect: region.w / region.h,
        })
        : { ok: false, error: "Could not read that selection." };
    } catch (err) {
      console.warn("[whiteboard] adjust failed:", err?.message || err);
      result = { ok: false, error: "Could not read that selection." };
    }

    if (!result.ok) {
      lumenSelectionRef.current = previous.join(",");
      setSelectedIds(previous);
      setAdjustTargetIds([]);
      setAdjustError(result.error);
      setAdjustBusy(false);
      return;
    }

    const region = regionOf(chosen, 8);
    saveHistory();

    if (result.path === "fill") {
      const byId = new Map((result.updates || []).map((u) => [u.id, u.text]));
      setElements((prev) => prev.map((el) => (byId.has(el.id) ? { ...el, content: byId.get(el.id) } : el)));
      lumenSelectionRef.current = previous.join(",");
      setSelectedIds(previous);
    } else if (result.path === "chart") {
      const chart = {
        id: uid(), type: "chart",
        chartType: result.chartType || "bar",
        title: result.title || "Chart",
        data: result.data || {},
        x: region.x, y: region.y,
        w: Math.max(region.w, 320), h: Math.max(region.h, 240),
      };
      setElements((prev) => [...prev.filter((el) => !previous.includes(el.id)), chart]);
      lumenSelectionRef.current = chart.id;
      setSelectedIds([chart.id]);
      setEditingChartId(chart.id);
    } else {
      const { src, storagePath } = (await persistGeneratedImage(result.image, userId, roomId)) || {};
      const [outW, outH] = String(result.size || "").split("x").map(Number);
      const box = fitInside(region, outW / outH);
      const img = {
        id: uid(), type: "image", src, storagePath, caption: result.caption || null,
        x: box.x, y: box.y, w: box.w, h: box.h,
      };
      setElements((prev) => [...prev.filter((el) => !previous.includes(el.id)), img]);
      lumenSelectionRef.current = img.id;
      setSelectedIds([img.id]);
    }

    setAdjustTargetIds([]);
    setAdjustBusy(false);
    setAdjustDone({ instruction, ids: previous, tick: historyTickRef.current });
  }, [elements, selectedIds, saveHistory, userId, roomId, roomContext]);

  const selectionKey = selectedIds.join(",");

  useEffect(() => {
    if (adjustBusy || retryRequest) return;
    if (lumenSelectionRef.current === selectionKey) return;
    lumenSelectionRef.current = null;
    setAdjustBarKey((k) => k + 1);
    setAdjustError(null);
    setAdjustDone(null);
  }, [selectionKey, adjustBusy, retryRequest]);

  const adjustResultShowing = Boolean(adjustDone) && adjustDone.tick === historyTick;

  const keepAdjust = useCallback(() => setAdjustDone(null), []);

  const retryAdjust = useCallback((nextInstruction) => {
    if (!adjustResultShowing) return;
    const { ids } = adjustDone;
    const instruction = nextInstruction || adjustDone.instruction;
    undo();
    lumenSelectionRef.current = ids.join(",");
    setSelectedIds(ids);
    setAdjustDone(null);
    setRetryRequest({ instruction, ids });
  }, [adjustResultShowing, adjustDone, undo]);

  useEffect(() => {
    if (!retryRequest) return;
    if (!retryRequest.ids.every((id) => elements.some((el) => el.id === id))) return;
    setRetryRequest(null);
    handleAdjust(retryRequest.instruction, retryRequest.ids);
  }, [retryRequest, elements, handleAdjust]);

  const adjustRegion = (() => {
    const ids = selectedIds.length ? selectedIds : adjustTargetIds;
    if (!ids.length || cropDraft || activeSlotId) return null;
    const chosen = elements.filter((el) => ids.includes(el.id));
    const region = regionOf(chosen, 0);
    if (!region) return null;
    return {
      x: region.x * vp.scale + vp.x,
      y: region.y * vp.scale + vp.y,
      w: region.w * vp.scale,
      h: region.h * vp.scale,
    };
  })();

  const adjustAnchor = adjustRegion
    ? {
        x: adjustRegion.x + adjustRegion.w / 2,
        y: adjustRegion.y,
        bottom: adjustRegion.y + adjustRegion.h,
      }
    : null;

  const placePhoto = useCallback((photo, at) => {
    const w = 240;
    const h = Math.round(w * (photo.height && photo.width ? photo.height / photo.width : 0.68));
    trackDownload(photo.downloadLocation);
    return {
      id: uid(), type: "image",
      src: photo.full, unsplashId: photo.id, alt: photo.alt,
      attribution: attributionFor(photo),
      x: at.x, y: at.y, w, h,
    };
  }, []);

  const handlePickPhoto = useCallback((photo) => {
    const { x, y } = viewOrigin();
    addElements(centreOnView([placePhoto(photo, { x, y })]));
  }, [viewOrigin, centreOnView, addElements, placePhoto]);

  const fillMoodboard = useCallback((photos) => {
    const { x, y } = viewOrigin();
    const groupId = uid();
    const chosen = photos.slice(0, 6);
    const els = chosen.map((photo, i) => {
      const col = i % 3;
      const row = Math.floor(i / 3);
      const el = placePhoto(photo, { x: x + col * 260, y: y + 46 + row * 220 });
      return { ...el, w: 240, h: 180, groupId };
    });
    addElements(centreOnView([
      { id: uid(), type: "text", x, y, w: 260, h: 34, content: "Moodboard", color: "#334155", size: 4, groupId },
      ...els,
    ]));
    setUnsplashOpen(false);
  }, [viewOrigin, centreOnView, addElements, placePhoto]);

  const patchSlot = useCallback((id, patch) => {
    setElements((prev) => prev.map((el) => (el.id === id ? { ...el, ...patch } : el)));
  }, []);

  const dropImageSlot = useCallback((pos) => {
    const el = {
      id: uid(), type: "image_slot",
      x: pos.x - 130, y: pos.y - 130, w: 260, h: 260,
      options: [], index: 0,
    };
    addElements([el]);
    setActiveSlotId(el.id);
    setTool("select");
  }, [addElements]);

  const handleGenerate = useCallback(async (id, prompt) => {
    patchSlot(id, { busy: true, error: null, prompt });

    const result = await generateImage({
      prompt,
      context: roomContext,
      userId,
      whiteboardId: whiteboardIdRef.current,
    });

    if (!result.ok) {
      patchSlot(id, { busy: false, error: result.error });
      if (result.cap) setImageUsage({ used: result.used, cap: result.cap });
      return;
    }

    setElements((prev) => prev.map((el) => {
      if (el.id !== id) return el;
      const options = [...(el.options || []), result.image];
      const details = [...(el.details || []), result.prompt || prompt];
      const captions = [...(el.captions || []), result.caption || null];
      return { ...el, busy: false, error: null, options, details, captions, index: options.length - 1 };
    }));
    if (result.cap) setImageUsage({ used: result.used, cap: result.cap });
  }, [patchSlot, roomContext, userId]);

  const cycleSlot = useCallback((id, dir) => {
    setElements((prev) => prev.map((el) => {
      if (el.id !== id) return el;
      const count = (el.options || []).length;
      if (!count) return el;
      return { ...el, index: (((el.index || 0) + dir) % count + count) % count };
    }));
  }, []);

  const commitSlot = useCallback(async (id) => {
    const slot = elements.find((el) => el.id === id);
    const chosen = (slot?.options || [])[slot?.index || 0];
    if (!chosen) return;

    patchSlot(id, { busy: true });
    const { src, storagePath } = (await persistGeneratedImage(chosen, userId, roomId)) || {};

    saveHistory();
    setElements((prev) => prev.map((el) => (el.id !== id ? el : {
      id: el.id, type: "image", src, storagePath, prompt: el.prompt,
      detail: (el.details || [])[el.index || 0],
      caption: (el.captions || [])[el.index || 0] || null,
      x: el.x, y: el.y, w: el.w, h: el.h,
    })));
    setActiveSlotId(null);
  }, [elements, patchSlot, userId, roomId, saveHistory]);

  const connectableAt = useCallback((pos) => {
    const hit = hitTest(pos);
    if (!hit) return null;
    if (hit.type === "connector" || hit.type === "artboard") return null;
    return hit;
  }, [hitTest]);

  const placeConnector = useCallback((pos) => {
    const hit = connectableAt(pos);
    if (!hit) { setConnectorFrom(null); return; }

    if (!connectorFrom) { setConnectorFrom(hit.id); return; }
    if (connectorFrom === hit.id) { setConnectorFrom(null); return; }

    addElements([{
      id: uid(), type: "connector",
      fromId: connectorFrom, toId: hit.id,
      arrow: tool === "connector-arrow",
      color, size: Math.max(2, size),
    }], { select: false });
    setConnectorFrom(hit.id);
  }, [connectableAt, connectorFrom, addElements, tool, color, size]);

  const withoutOrphans = useCallback((list) => {
    const ids = new Set(list.map((el) => el.id));
    return list.filter((el) =>
      el.type !== "connector" || (ids.has(el.fromId) && ids.has(el.toId)));
  }, []);

  const cycleGrid = useCallback(() => {
    setGridMode((m) => GRID_MODES[(GRID_MODES.indexOf(m) + 1) % GRID_MODES.length]);
  }, []);

  const zoomBy = useCallback((factor) => {
    setVp((v) => ({ ...v, scale: Math.max(MIN_SCALE, Math.min(MAX_SCALE, v.scale * factor)) }));
  }, []);

  const clearBoard = useCallback(() => {
    if (!elements.length) return;
    saveHistory();
    setElements([]);
    setSelectedIds([]);
  }, [elements.length, saveHistory]);

  const beginCrop = useCallback(() => {
    const el = elements.find((e) => e.id === selectedIds[0]);
    if (!el || el.type !== "image") return;
    setCropDraft({ id: el.id, x: el.x, y: el.y, w: el.w, h: el.h });
  }, [elements, selectedIds]);

  const applyCrop = useCallback(() => {
    if (!cropDraft) return;
    const el = elements.find((e) => e.id === cropDraft.id);
    if (!el) { setCropDraft(null); return; }

    const prev = el.crop || { sx: 0, sy: 0, sw: 1, sh: 1 };
    const fx = (cropDraft.x - el.x) / el.w;
    const fy = (cropDraft.y - el.y) / el.h;
    const fw = cropDraft.w / el.w;
    const fh = cropDraft.h / el.h;

    saveHistory();
    setElements((list) => list.map((e) => e.id !== el.id ? e : {
      ...e,
      x: cropDraft.x, y: cropDraft.y, w: cropDraft.w, h: cropDraft.h,
      crop: {
        sx: prev.sx + fx * prev.sw,
        sy: prev.sy + fy * prev.sh,
        sw: prev.sw * fw,
        sh: prev.sh * fh,
      },
    }));
    setCropDraft(null);
  }, [cropDraft, elements, saveHistory]);

  const dropChart = useCallback((chartType) => {
    const { x, y } = viewOrigin();
    const [el] = centreOnView([makeChart(chartType, x, y, uid())]);
    addElements([el]);
    setEditingChartId(el.id);
  }, [viewOrigin, centreOnView, addElements]);

  const openChartEditor = useCallback((id) => {
    beginInteraction();
    setEditingChartId(id);
  }, [beginInteraction]);

  const updateChart = useCallback((id, data, patch) => {
    commitInteraction();
    setElements((prev) => prev.map((el) => {
      if (el.id !== id) return el;
      return { ...el, ...(patch || {}), ...(data ? { data } : {}) };
    }));
  }, [commitInteraction]);

  const handleAction = useCallback((actionId) => {
    switch (actionId) {
      case "undo":         undo(); break;
      case "redo":         redo(); break;
      case "clear":        clearBoard(); break;
      case "grid":         cycleGrid(); break;
      case "zoom-in":      zoomBy(1.25); break;
      case "zoom-out":     zoomBy(0.8); break;
      case "image-upload": fileInputRef.current?.click(); break;
      case "export":      setExportOpen(true); break;
      case "unsplash":    setUnsplashOpen(true); break;
      case "chart-data": {
        const chart = elements.find((el) => el.id === selectedIds[0] && el.type === "chart");
        if (chart) openChartEditor(chart.id);
        break;
      }
      default: break;
    }
  }, [undo, redo, clearBoard, cycleGrid, zoomBy, elements, selectedIds, openChartEditor]);

  const handleTool = useCallback((toolId) => {
    if (!CONNECTOR_TOOLS.includes(toolId)) setConnectorFrom(null);
    if (CHART_TOOLS[toolId]) {
      setTool("select");
      dropChart(CHART_TOOLS[toolId]);
      return;
    }
    if (toolId === "crop") {
      setTool("select");
      beginCrop();
      return;
    }
    setCropDraft(null);
    setTool(toolId);
  }, [beginCrop, dropChart]);

  const handleFramework = useCallback((frameworkId) => {
    const { x, y } = viewOrigin();
    const els = buildFramework(frameworkId, x, y);
    const tag = toolbarFrameworkTag(frameworkId, els[0]?.groupId);
    addElements(centreOnView(els.map((el) => ({ ...el, ...tag }))));
  }, [viewOrigin, centreOnView, addElements]);

  const dropMoodboard = useCallback(() => {
    const { x, y } = viewOrigin();
    const groupId = uid();
    const els = [
      { id: uid(), type: "text", x, y, w: 260, h: 34, content: "Moodboard", color: "#334155", size: 4 },
    ];
    for (let row = 0; row < 2; row += 1) {
      for (let col = 0; col < 3; col += 1) {
        els.push({
          id: uid(), type: "frame",
          x: x + col * 210, y: y + 46 + row * 170,
          w: 195, h: 155, color: "#94A3B8", size: 2,
        });
      }
    }
    addElements(centreOnView(els.map((el) => ({ ...el, groupId }))));
  }, [viewOrigin, centreOnView, addElements]);

  const handleCreativeStart = useCallback((id) => {
    if (id === "map-idea")  { handleFramework("mindmap"); return; }
    if (id === "moodboard") { setUnsplashOpen(true); return; }
    if (id === "help-me-start") onAskLumen?.();
  }, [handleFramework, dropMoodboard, onAskLumen]);

  const creativeStartRef = useRef(handleCreativeStart);
  creativeStartRef.current = handleCreativeStart;

  useEffect(() => {
    onStartsChange?.(CREATIVE_STARTS.map((start) => ({
      ...start, onClick: () => creativeStartRef.current(start.id),
    })));
    return () => onStartsChange?.(null);
  }, [onStartsChange]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const place = (src, storagePath) => {
      const img = new Image();
      img.onload = () => {
        const w = Math.min(360, img.naturalWidth);
        const h = (img.naturalHeight / img.naturalWidth) * w;
        const { x, y } = viewOrigin();
        addElements(centreOnView([{ id: uid(), type: "image", src, storagePath, x, y, w, h }]));
      };
      img.onerror = () => console.warn("[whiteboard] image failed to load");
      img.src = src;
    };

    if (userId && roomId) {
      try {
        const { storagePath } = await uploadRoomFile(userId, roomId, file);
        const src = await getRoomFileUrl(storagePath);
        place(src, storagePath);
        return;
      } catch (err) {
        console.warn("[whiteboard] upload failed, keeping image inline:", err?.message || err);
      }
    }

    const reader = new FileReader();
    reader.onload = (ev) => place(ev.target.result, null);
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement;
      return Boolean(editingTextId) || el?.isContentEditable
        || ["INPUT", "TEXTAREA"].includes(el?.tagName);
    };

    const down = (e) => {
      if (e.key === "Escape") {
        if (connectorFrom) { setConnectorFrom(null); return; }
        if (cropDraft) { setCropDraft(null); return; }
        setSelectedIds([]);
        return;
      }
      if (isTyping()) return;

      if (e.key === " ") { setSpaceDown(true); e.preventDefault(); }
      if ((e.metaKey || e.ctrlKey) && e.key === "z") { e.preventDefault(); undo(); }
      if ((e.metaKey || e.ctrlKey) && e.key === "y") { e.preventDefault(); redo(); }

      if (e.key === "Enter" && cropDraft) { e.preventDefault(); applyCrop(); return; }

      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedIds.length) {
          saveHistory();
          setElements((prev) => withoutOrphans(prev.filter((el) => !selectedIds.includes(el.id))));
          setSelectedIds([]);
        }
      }

      if (!e.metaKey && !e.ctrlKey && KEY_TO_TOOL[e.key]) setTool(KEY_TO_TOOL[e.key]);
    };

    const up = (e) => { if (e.key === " ") setSpaceDown(false); };

    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [undo, redo, selectedIds, saveHistory, editingTextId, cropDraft, applyCrop, connectorFrom]);

  const onWheel = useCallback((e) => {
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setVp((prev) => {
      const ns = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev.scale * delta));
      const r = ns / prev.scale;
      return { scale: ns, x: mx - r * (mx - prev.x), y: my - r * (my - prev.y) };
    });
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onWheel]);

  const placeStamp = (pos) => {
    const dims = STAMP_SIZES[tool] || { w: 120, h: 100 };
    const base = {
      id: uid(), type: tool,
      x: pos.x - dims.w / 2, y: pos.y - dims.h / 2,
      w: dims.w, h: dims.h, color, size,
    };

    if (tool === "sticky") {
      const el = {
        ...base,
        bg: STICKY_COLORS[Math.floor(Math.random() * STICKY_COLORS.length)],
        color: "#1E293B", size: 2.4, content: "",
        rotate: Math.round((Math.random() * 4 - 2) * 10) / 10,
      };
      addElements([el]);
      setEditingTextId(el.id);
      return;
    }
    if (tool === "sticker" || tool === "emoji") {
      addElements([{ ...base, glyph: glyph || (tool === "emoji" ? "😀" : "★") }]);
      return;
    }
    if (tool === "tape") {
      addElements([{ ...base, bg: color, rotate: -4 }]);
      return;
    }
    addElements([base]);
  };

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    const pos = getCanvasPos(e);
    const isPan = tool === "hand" || spaceDown;

    if (editingTextId) {
      const open = containerRef.current?.querySelector('[contenteditable="true"]');
      if (open) handleEditDone(editingTextId, open.textContent);
      else setEditingTextId(null);
    }

    if (cropDraft) {
      const inside = pos.x >= cropDraft.x && pos.x <= cropDraft.x + cropDraft.w
                  && pos.y >= cropDraft.y && pos.y <= cropDraft.y + cropDraft.h;
      if (!inside) applyCrop();
      return;
    }

    if (isPan) {
      setPanning({ startX: e.clientX, startY: e.clientY, startVpX: vp.x, startVpY: vp.y });
      return;
    }

    if (e.metaKey || e.ctrlKey) {
      setSelectedIds([]);
      setRubberBand({ startPos: pos, endPos: pos });
      return;
    }

    if (tool === IMAGE_SLOT_TOOL) { dropImageSlot(pos); return; }
    if (CONNECTOR_TOOLS.includes(tool)) { placeConnector(pos); return; }
    if (STAMP_TOOLS.includes(tool)) { placeStamp(pos); return; }

    if (tool === "select") {
      const hit = hitTest(pos);
      if (hit) {
        let ids = [hit.id];
        if (hit.groupId) ids = elements.filter((el) => el.groupId === hit.groupId).map((el) => el.id);
        if (e.shiftKey) {
          setSelectedIds((prev) => prev.includes(hit.id)
            ? prev.filter((id) => !ids.includes(id))
            : [...new Set([...prev, ...ids])]);
          return;
        }
        const newIds = selectedIds.includes(hit.id) ? selectedIds : ids;
        setSelectedIds(newIds);
        const offsets = elements.filter((el) => newIds.includes(el.id)).map((el) => {
          if (el.type === "stroke" && el.points?.length) {
            return { id: el.id, isStroke: true, offX: pos.x - el.points[0].x, offY: pos.y - el.points[0].y };
          }
          return { id: el.id, offX: pos.x - el.x, offY: pos.y - el.y };
        });
        beginInteraction();
        setDragging({ offsets });
      } else if (!e.shiftKey) {
        setSelectedIds([]);
        setPanning({ startX: e.clientX, startY: e.clientY, startVpX: vp.x, startVpY: vp.y });
      }
      return;
    }

    if (tool === "pen" || tool === "highlighter") {
      saveHistory();
      setCurrentStroke({
        id: uid(), type: "stroke", tool, color,
        size: tool === "highlighter" ? Math.max(size * 5, 12) : size,
        points: [pos],
      });
      setDrawing(true);
      return;
    }

    if (tool === "eraser") {
      beginInteraction();
      setDrawing(true);
      const hit = hitTest(pos);
      if (hit) {
        commitInteraction();
        setElements((prev) => withoutOrphans(prev.filter((el) => el.id !== hit.id)));
      }
      return;
    }

    if (tool === "text") {
      e.preventDefault();
      const fontSize = Math.max(12, size * 5);
      const el = {
        id: uid(), type: "text",
        x: pos.x, y: pos.y,
        w: 240, h: Math.ceil(fontSize * 1.35) + 10,
        content: "", color, size,
      };
      addElements([el]);
      setEditingTextId(el.id);
      setTool("select");
      return;
    }

    if (LINE_TOOLS.includes(tool))  { setDrawingLine({ type: tool, startPos: pos, endPos: pos }); return; }
    if (SHAPE_TOOLS.includes(tool) || tool === "artboard") {
      setDrawingShape({ type: tool, startPos: pos, endPos: pos });
      return;
    }
  };

  const onMouseMove = (e) => {
    if (connectorFrom) setPointerPos(getCanvasPos(e));
    if (tool === "select" && !dragging && !panning && !resizing && !rubberBand) {
      const empty = !hitTest(getCanvasPos(e));
      setOverEmpty((prev) => (prev === empty ? prev : empty));
    }
    if (cropHandle && cropDraft) {
      const pos = getCanvasPos(e);
      setCropDraft((c) => {
        const el = elements.find((x) => x.id === c.id);
        if (!el) return c;
        let { x, y, w, h } = c;
        const right = x + w;
        const bottom = y + h;
        if (cropHandle.includes("w")) { x = Math.min(Math.max(pos.x, el.x), right - 20); w = right - x; }
        if (cropHandle.includes("n")) { y = Math.min(Math.max(pos.y, el.y), bottom - 20); h = bottom - y; }
        if (cropHandle.includes("e")) { w = Math.min(Math.max(pos.x - x, 20), el.x + el.w - x); }
        if (cropHandle.includes("s")) { h = Math.min(Math.max(pos.y - y, 20), el.y + el.h - y); }
        return { ...c, x, y, w, h };
      });
      return;
    }

    if (panning) {
      setVp((prev) => ({
        ...prev,
        x: panning.startVpX + (e.clientX - panning.startX),
        y: panning.startVpY + (e.clientY - panning.startY),
      }));
      return;
    }

    if (resizing) {
      const pos = getCanvasPos(e);
      const dx = pos.x - resizing.startPos.x;
      const dy = pos.y - resizing.startPos.y;
      const { x: ox, y: oy, w: ow, h: oh } = resizing.startEl;
      let nx = ox, ny = oy, nw = ow, nh = oh;
      const hIdx = resizing.handle;
      if ([0, 1, 2].includes(hIdx)) { ny = oy + dy; nh = Math.max(20, oh - dy); }
      if ([4, 5, 6].includes(hIdx)) { nh = Math.max(20, oh + dy); }
      if ([0, 6, 7].includes(hIdx)) { nx = ox + dx; nw = Math.max(20, ow - dx); }
      if ([2, 3, 4].includes(hIdx)) { nw = Math.max(20, ow + dx); }
      const box = resizing.signW < 0
        ? { x: nx + nw, w: -nw }
        : { x: nx, w: nw };
      const boxY = resizing.signH < 0
        ? { y: ny + nh, h: -nh }
        : { y: ny, h: nh };
      setElements((prev) => prev.map((el) => {
        if (el.id !== resizing.id) return el;
        const next = { ...el, ...box, ...boxY };
        if (el.type === "text" && resizing.startEl.h > 0) {
          const scale = Math.abs(nh) / resizing.startEl.h;
          next.size = Math.max(2.4, Math.min(40, (resizing.startSize || el.size) * scale));
        }
        return next;
      }));
      return;
    }

    if (dragging) {
      const pos = getCanvasPos(e);
      commitInteraction();
      setElements((prev) => prev.map((el) => {
        const off = dragging.offsets.find((o) => o.id === el.id);
        if (!off) return el;
        if (off.isStroke && el.points) {
          const dx = pos.x - off.offX - el.points[0].x;
          const dy = pos.y - off.offY - el.points[0].y;
          return { ...el, points: el.points.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
        }
        return { ...el, x: pos.x - off.offX, y: pos.y - off.offY };
      }));
      return;
    }

    if (rubberBand)   { setRubberBand((p) => ({ ...p, endPos: getCanvasPos(e) })); return; }
    if (drawingLine)  { setDrawingLine((p) => ({ ...p, endPos: getCanvasPos(e) })); return; }
    if (drawingShape) { setDrawingShape((p) => ({ ...p, endPos: getCanvasPos(e) })); return; }

    if (drawing && currentStroke) {
      const pos = getCanvasPos(e);
      setCurrentStroke((p) => ({ ...p, points: [...p.points, pos] }));
      return;
    }

    if (drawing && tool === "eraser") {
      const hit = hitTest(getCanvasPos(e));
      if (hit) {
        commitInteraction();
        setElements((prev) => withoutOrphans(prev.filter((el) => el.id !== hit.id)));
      }
    }
  };

  const onMouseUp = () => {
    if (cropHandle) { setCropHandle(null); return; }
    if (panning)  { setPanning(null);  return; }
    if (resizing) { setResizing(null); return; }
    if (dragging) { setDragging(null); return; }

    if (rubberBand) {
      const { startPos, endPos } = rubberBand;
      const rx1 = Math.min(startPos.x, endPos.x), ry1 = Math.min(startPos.y, endPos.y);
      const rx2 = Math.max(startPos.x, endPos.x), ry2 = Math.max(startPos.y, endPos.y);
      const hit = elements.filter((el) => {
        const b = elementBounds(el);
        return b && b.x < rx2 && b.x + b.w > rx1 && b.y < ry2 && b.y + b.h > ry1;
      });
      const groupIds = new Set(hit.filter((el) => el.groupId).map((el) => el.groupId));
      const groupMembers = elements.filter((el) => el.groupId && groupIds.has(el.groupId));
      setSelectedIds([...new Set([...hit, ...groupMembers].map((el) => el.id))]);
      setRubberBand(null);
      return;
    }

    if (drawingLine) {
      const { type, startPos, endPos } = drawingLine;
      const dx = endPos.x - startPos.x, dy = endPos.y - startPos.y;
      if (Math.sqrt(dx * dx + dy * dy) > 5) {
        addElements([{ id: uid(), type, x: startPos.x, y: startPos.y, w: dx, h: dy, color, size }]);
      }
      setDrawingLine(null);
      return;
    }

    if (drawingShape) {
      const { type, startPos, endPos } = drawingShape;
      const x = Math.min(startPos.x, endPos.x), y = Math.min(startPos.y, endPos.y);
      const w = Math.abs(endPos.x - startPos.x), h = Math.abs(endPos.y - startPos.y);
      if (w > 5 && h > 5) {
        if (type === "artboard") {
          const n = elements.filter((el) => el.type === "artboard").length + 1;
          addElements([{ id: uid(), type: "artboard", x, y, w, h, name: `Artboard ${n}` }]);
          setTool("select");
        } else {
          addElements([{ id: uid(), type, x, y, w, h, color, size }]);
        }
      }
      setDrawingShape(null);
      return;
    }

    if (drawing) {
      if (currentStroke?.points?.length > 1) {
        setElements((prev) => [...prev, currentStroke]);
      }
      setDrawing(false);
      setCurrentStroke(null);
    }
  };

  const onDoubleClick = (e) => {
    if (tool !== "select" && tool !== "pen" && tool !== "hand") return;
    e.preventDefault();
    const pos = getCanvasPos(e);
    const words = [...elements].reverse().find((el) => {
      if (el.type !== "text" && el.type !== "sticky") return false;
      const b = elementBounds(el);
      return b && pos.x >= b.x && pos.x <= b.x + b.w && pos.y >= b.y && pos.y <= b.y + b.h;
    });
    const hit = words || hitTest(pos);
    if (hit) {
      if (hit.type === "text" || hit.type === "sticky") setEditingTextId(hit.id);
      if (hit.type === "chart") openChartEditor(hit.id);
      if (hit.type === "image_slot") setActiveSlotId(hit.id);
      return;
    }
    const el = {
      id: uid(), type: "text",
      x: pos.x - 75, y: pos.y - 20,
      w: 200, h: Math.ceil(15 * 1.35) + 10,
      content: "", color: "#1E293B", size: 3,
    };
    addElements([el]);
    setEditingTextId(el.id);
    setTool("select");
  };

  const handleHandleDown = useCallback((e, handleIndex) => {
    const pos = getCanvasPos(e);
    const el = elements.find((x) => x.id === selectedIds[0]);
    if (!el) return;
    saveHistory();
    setResizing({
      id: el.id, handle: handleIndex, startPos: pos,
      startEl: elementBounds(el),
      startSize: el.size,
      signW: (el.w ?? 200) < 0 ? -1 : 1,
      signH: (el.h ?? 100) < 0 ? -1 : 1,
    });
  }, [selectedIds, elements, getCanvasPos, saveHistory]);

  const handleEditDone = (id, newContent) => {
    setEditingTextId(null);
    const text = (newContent || "").trim();
    const el = elements.find((e) => e.id === id);
    if (text || el?.type === "sticky") {
      setElements((prev) => prev.map((e) => e.id === id ? { ...e, content: text } : e));
    } else {
      setElements((prev) => prev.filter((e) => e.id !== id));
      setSelectedIds((prev) => prev.filter((sid) => sid !== id));
    }
  };

  const activeTool = spaceDown ? "hand" : tool;
  const cursorMap = {
    select: "default", hand: "grab", pen: "crosshair", highlighter: "crosshair",
    eraser: "cell", text: "text", rect: "crosshair", circle: "crosshair",
    triangle: "crosshair", line: "crosshair", arrow: "crosshair",
  };
  const cursor = dragging || panning ? "grabbing"
    : STAMP_TOOLS.includes(activeTool) ? "copy"
    : activeTool === "select" && overEmpty ? "grab"
    : (cursorMap[activeTool] || "default");

  const selectedEl = selectedIds.length === 1 && !cropDraft
    ? elements.find((e) => e.id === selectedIds[0])
    : null;

  const strokePath = currentStroke?.points?.length > 1
    ? currentStroke.points.reduce((acc, p, i) => i === 0 ? `M ${p.x} ${p.y}` : `${acc} L ${p.x} ${p.y}`, "")
    : null;

  const lumenZoneX = CANVAS_W * 0.7;
  const saveLabel = { saving: "Saving…", saved: "Saved", error: "Save failed" }[saveState] || "";

  return (
    <div className="flex flex-col h-full w-full bg-[#E8E4DE] font-['DM_Sans'] overflow-hidden rounded-2xl">
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <div className="flex items-center justify-between p-[10px_16px] bg-white/[0.88] backdrop-blur-xl flex-shrink-0 border-b border-black/[0.06]">
        <div className="flex items-center gap-[10px]">
          <span className="text-[16px]">🖊</span>
          <span className="text-[14px] font-bold text-slate-800">Whiteboard</span>
        </div>
        <div className="flex items-center gap-3">
          {saveLabel && (
            <span className="text-[10px] font-medium" style={{ color: saveState === "error" ? "#EF4444" : "#94A3B8" }}>
              {saveLabel}
            </span>
          )}
          <button
            type="button"
            onClick={() => (historyOpen ? setHistoryOpen(false) : openHistory())}
            title="Version history"
            className="text-[11px] font-semibold px-2 py-[3px] rounded-md cursor-pointer border transition-colors"
            style={{
              color: historyOpen ? TEAL : "#64748B",
              borderColor: historyOpen ? "rgba(77,184,181,0.45)" : "rgba(0,0,0,0.10)",
              background: historyOpen ? "rgba(77,184,181,0.12)" : "transparent",
            }}
          >
            History
          </button>
          <span className="text-[10px] text-slate-400 font-mono">{Math.round(vp.scale * 100)}%</span>
          <button onClick={handleClose} className="bg-transparent border-none text-slate-400 text-[18px] cursor-pointer">✕</button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex items-stretch gap-[10px] p-[12px_14px_0]">
        <WhiteboardToolbar
          category={category} onCategory={setCategory}
          tool={tool} onTool={handleTool}
          color={color} onColor={setColor}
          size={size} onSize={setSize}
          gridMode={gridMode}
          canUndo={history.length > 0}
          canRedo={redoStack.length > 0}
          onAction={handleAction}
          onFramework={handleFramework}
          glyph={glyph} onGlyph={setGlyph}
        />

        <div className="flex-1 min-w-0 overflow-hidden flex flex-col rounded-lg border-[6px] border-[#C8C8CC] shadow-[inset_0_2px_4px_rgba(255,255,255,0.8),0_4px_16px_rgba(0,0,0,0.15)] bg-[#D0D0D4] relative">
          <div className="absolute inset-0 rounded pointer-events-none z-10"
            style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.35) 0%, transparent 35%)" }} />

          <div ref={containerRef}
            className="flex-1 overflow-hidden relative"
            style={{ background: BOARD_BG, cursor }}
            onMouseDown={onMouseDown} onMouseMove={onMouseMove}
            onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
            onDoubleClick={onDoubleClick}>

            <svg ref={svgRef} width="100%" height="100%" className="absolute inset-0 pointer-events-none">
              <g transform={`translate(${vp.x},${vp.y}) scale(${vp.scale})`}>
                <defs>
                  <pattern id="wb-dots" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="0" cy="0" r="0.8" fill="rgba(0,0,0,0.07)" />
                  </pattern>
                  <pattern id="wb-lines" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M40 0H0V40" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="1" />
                  </pattern>
                </defs>
                {gridMode !== "off" && (
                  <rect data-export-ignore x={0} y={0} width={CANVAS_W} height={CANVAS_H}
                    fill={`url(#wb-${gridMode})`} />
                )}

                <g data-export-ignore>
                <rect x={lumenZoneX} y={0} width={CANVAS_W - lumenZoneX} height={CANVAS_H} fill="rgba(77,184,181,0.025)" />
                <line x1={lumenZoneX} y1={0} x2={lumenZoneX} y2={CANVAS_H}
                  stroke="rgba(77,184,181,0.18)" strokeWidth={1.5} strokeDasharray="10 5" />
                <text x={lumenZoneX + 18} y={32} fill="rgba(77,184,181,0.45)"
                  className="text-[14px] font-['DM_Sans'] font-semibold">✦ Lumen Ideas</text>
                </g>

                <g style={{ pointerEvents: tool === "select" ? "auto" : "none" }}>
                  {[...elements]
                    .sort((a, b) => (a.type === "artboard" ? -1 : 0) - (b.type === "artboard" ? -1 : 0))
                    .map((el) => {
                    const isSelected = selectedIds.includes(el.id);
                    const isDragging = dragging && isSelected;
                    const b = elementBounds(el);
                    return (
                      <g key={el.id}
                        style={{
                          cursor: tool === "select" ? (isSelected ? "grab" : "pointer") : "inherit",
                          filter: isDragging ? "drop-shadow(0 8px 16px rgba(0,0,0,0.22))" : "none",
                        }}
                        onMouseDown={(e) => {
                          if (editingTextId === el.id) { e.stopPropagation(); return; }
                          if (tool !== "select" || e.metaKey || e.ctrlKey || cropDraft) return;
                          e.stopPropagation();
                          const pos = getCanvasPos(e);
                          let ids = [el.id];
                          if (el.groupId) ids = elements.filter((em) => em.groupId === el.groupId).map((em) => em.id);
                          const newIds = e.shiftKey
                            ? (selectedIds.includes(el.id)
                                ? selectedIds.filter((id) => !ids.includes(id))
                                : [...new Set([...selectedIds, ...ids])])
                            : (selectedIds.includes(el.id) ? selectedIds : ids);
                          setSelectedIds(newIds);
                          const offsets = elements.filter((em) => newIds.includes(em.id)).map((em) => (
                            em.type === "stroke" && em.points?.length
                              ? { id: em.id, isStroke: true, offX: pos.x - em.points[0].x, offY: pos.y - em.points[0].y }
                              : { id: em.id, offX: pos.x - em.x, offY: pos.y - em.y }
                          ));
                          beginInteraction();
                          setDragging({ offsets });
                        }}>
                        <BoardElement el={el} editingTextId={editingTextId} byId={elementsById}
                          onEdit={setEditingTextId} onEditDone={handleEditDone} />
                        {isSelected && b && el.type !== "stroke" && (
                          <rect data-export-ignore x={b.x - 2} y={b.y - 2} width={b.w + 4} height={b.h + 4}
                            fill="none" stroke={TEAL} strokeWidth={1.5} strokeDasharray="5 3"
                            className="pointer-events-none" />
                        )}
                      </g>
                    );
                  })}
                </g>

                {strokePath && (
                  <path d={strokePath} stroke={color}
                    strokeWidth={tool === "highlighter" ? Math.max(size * 5, 12) : size}
                    fill="none" strokeLinecap="round" strokeLinejoin="round"
                    opacity={tool === "highlighter" ? 0.35 : 1} />
                )}

                {drawingLine && (() => {
                  const { type, startPos, endPos } = drawingLine;
                  const common = { stroke: color, strokeWidth: size, strokeLinecap: "round", fill: "none", opacity: 0.7 };
                  if (type === "line") return <line x1={startPos.x} y1={startPos.y} x2={endPos.x} y2={endPos.y} {...common} />;
                  const dx = endPos.x - startPos.x, dy = endPos.y - startPos.y;
                  const ang = Math.atan2(dy, dx), len = 16;
                  return (
                    <g {...common}>
                      <line x1={startPos.x} y1={startPos.y} x2={endPos.x} y2={endPos.y} />
                      <line x1={endPos.x} y1={endPos.y} x2={endPos.x - len * Math.cos(ang - 0.4)} y2={endPos.y - len * Math.sin(ang - 0.4)} />
                      <line x1={endPos.x} y1={endPos.y} x2={endPos.x - len * Math.cos(ang + 0.4)} y2={endPos.y - len * Math.sin(ang + 0.4)} />
                    </g>
                  );
                })()}

                {drawingShape && (() => {
                  const { type, startPos, endPos } = drawingShape;
                  const x = Math.min(startPos.x, endPos.x), y = Math.min(startPos.y, endPos.y);
                  const w = Math.abs(endPos.x - startPos.x), h = Math.abs(endPos.y - startPos.y);
                  const s = { stroke: color, strokeWidth: size, fill: "none", opacity: 0.7, strokeDasharray: "5 3" };
                  if (type === "rect")     return <rect x={x} y={y} width={w} height={h} {...s} />;
                  if (type === "circle")   return <ellipse cx={x + w / 2} cy={y + h / 2} rx={w / 2} ry={h / 2} {...s} />;
                  if (type === "triangle") return <polygon points={`${x + w / 2},${y} ${x + w},${y + h} ${x},${y + h}`} {...s} />;
                  if (type === "artboard") {
                    return <rect x={x} y={y} width={w} height={h} rx={6} fill="rgba(77,184,181,0.05)"
                      stroke={TEAL} strokeWidth={1.5} strokeDasharray="9 6" />;
                  }
                  return null;
                })()}

                {rubberBand && (() => {
                  const { startPos, endPos } = rubberBand;
                  return (
                    <rect data-export-ignore
                      x={Math.min(startPos.x, endPos.x)} y={Math.min(startPos.y, endPos.y)}
                      width={Math.abs(endPos.x - startPos.x)} height={Math.abs(endPos.y - startPos.y)}
                      fill="rgba(77,184,181,0.06)" stroke={TEAL} strokeWidth={1}
                      strokeDasharray="4 2" className="pointer-events-none" />
                  );
                })()}

                {connectorFrom && CONNECTOR_TOOLS.includes(tool) && (() => {
                  const from = elementsById.get(connectorFrom);
                  const b = from && elementBounds(from);
                  if (!b) return null;
                  const centre = { x: b.x + b.w / 2, y: b.y + b.h / 2 };
                  return (
                    <g data-export-ignore className="pointer-events-none">
                      <rect x={b.x - 3} y={b.y - 3} width={b.w + 6} height={b.h + 6} rx={4}
                        fill="none" stroke={TEAL} strokeWidth={2} />
                      {pointerPos && (
                        <line x1={centre.x} y1={centre.y} x2={pointerPos.x} y2={pointerPos.y}
                          stroke={TEAL} strokeWidth={1.5} strokeDasharray="5 4" opacity={0.7} />
                      )}
                    </g>
                  );
                })()}

                {cropDraft && (() => {
                  const el = elements.find((e) => e.id === cropDraft.id);
                  if (!el) return null;
                  const handles = [
                    ["nw", cropDraft.x, cropDraft.y],
                    ["ne", cropDraft.x + cropDraft.w, cropDraft.y],
                    ["se", cropDraft.x + cropDraft.w, cropDraft.y + cropDraft.h],
                    ["sw", cropDraft.x, cropDraft.y + cropDraft.h],
                  ];
                  return (
                    <g data-export-ignore className="pointer-events-auto">
                      <path
                        d={`M${el.x},${el.y}h${el.w}v${el.h}h${-el.w}z M${cropDraft.x},${cropDraft.y}v${cropDraft.h}h${cropDraft.w}v${-cropDraft.h}z`}
                        fill="rgba(0,0,0,0.45)" fillRule="evenodd" className="pointer-events-none" />
                      <rect x={cropDraft.x} y={cropDraft.y} width={cropDraft.w} height={cropDraft.h}
                        fill="none" stroke="white" strokeWidth={1.5} className="pointer-events-none" />
                      {handles.map(([pos, hx, hy]) => (
                        <rect key={pos} x={hx - 7} y={hy - 7} width={14} height={14} rx={2}
                          fill="white" stroke={TEAL} strokeWidth={1.5}
                          style={{ cursor: `${pos}-resize` }}
                          onMouseDown={(e) => { e.stopPropagation(); setCropHandle(pos); }} />
                      ))}
                    </g>
                  );
                })()}

                {selectedEl && (
                  <g data-export-ignore className="pointer-events-auto">
                    <SelectionHandles el={selectedEl} onHandleDown={handleHandleDown} />
                  </g>
                )}
              </g>
            </svg>

            {loading && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-[12px] text-slate-400">Loading board…</span>
              </div>
            )}

            {!loading && elements.length === 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-[40px] opacity-10">✏️</span>
                <div className="text-[13px] text-slate-400 mt-[10px]">Pick a tool and start thinking</div>
                <div className="text-[11px] text-slate-300 mt-1">Scroll to zoom · Space+drag to pan · V to select</div>
              </div>
            )}

            {adjustBusy && adjustRegion && (
              <div
                className="absolute pointer-events-none rounded-lg animate-pulse"
                style={{
                  left: adjustRegion.x - 4, top: adjustRegion.y - 4,
                  width: adjustRegion.w + 8, height: adjustRegion.h + 8,
                  border: `2px dashed ${TEAL}`,
                }}
              />
            )}

            <AdjustBar
              key={adjustBarKey}
              screenPos={adjustAnchor}
              busy={adjustBusy}
              error={adjustError}
              result={adjustResultShowing}
              onSubmit={handleAdjust}
              onDismiss={() => setAdjustError(null)}
              onKeep={keepAdjust}
              onRetry={retryAdjust}
            />

            {unsplashOpen && (
              <UnsplashPanel
                onSearch={searchPhotos}
                onPick={handlePickPhoto}
                onFillMoodboard={fillMoodboard}
                onClose={() => setUnsplashOpen(false)}
              />
            )}

            {activeSlotId && (
              <ImagePromptBar
                slot={elements.find((el) => el.id === activeSlotId)}
                usage={imageUsage}
                onGenerate={handleGenerate}
                onCycle={cycleSlot}
                onCommit={commitSlot}
                onClose={() => setActiveSlotId(null)}
              />
            )}

            {exportOpen && (
              <ExportPanel
                selected={selectedElements}
                scope={exportScope}
                onScope={setExportScope}
                onExport={handleExport}
                busy={exportBusy}
                error={exportError}
                onClose={() => { setExportOpen(false); setExportError(null); }}
              />
            )}

            {historyOpen && (
              <VersionHistory
                versions={versions}
                loading={versionsLoading}
                busyId={restoringId}
                saving={savingVersion}
                unavailable={!userId || !roomId}
                onRestore={handleRestore}
                onSaveVersion={() => handleSaveVersion()}
                onClose={() => setHistoryOpen(false)}
              />
            )}

            {editingChartId && (
              <ChartDataEditor
                el={elements.find((el) => el.id === editingChartId)}
                onChange={updateChart}
                onClose={() => setEditingChartId(null)}
              />
            )}

            {cropDraft && (
              <div
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-2 rounded-lg bg-white/95 shadow-[0_4px_16px_rgba(0,0,0,0.18)] border border-black/[0.06]">
                <span className="text-[11px] text-slate-500">Drag the corners to crop</span>
                <button onClick={applyCrop}
                  className="text-[11px] font-semibold px-2 py-1 rounded-md cursor-pointer border-none"
                  style={{ background: TEAL, color: "white" }}>Apply</button>
                <button onClick={() => setCropDraft(null)}
                  className="text-[11px] font-semibold px-2 py-1 rounded-md cursor-pointer bg-transparent border border-black/10 text-slate-500">Cancel</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />


      <style>{`
        [data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: #94A3B8;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}
